import { Flex, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { newCOLORS } from '../../../../styles/colors';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';

type DashboardTemplateActionBarProps = {
  description: string;
  isEditingLayout: boolean;
  onEditDetails: () => void;
  onStartEditLayout: () => void;
  onAddWidget: () => void;
  onCancelLayout: () => void;
  onResetLayout: () => void;
  onSaveLayout: () => void;
};

/**
 * Toolbar shown above the template preview grid. Renders the live description
 * on the left and the action buttons on the right; the right side toggles
 * between view-mode (Edit / Edit Layout / Add Widget) and layout-edit-mode
 * (Cancel / Reset Layout / Save Layout) based on `isEditingLayout`.
 *
 * @param {DashboardTemplateActionBarProps} props - Description text + the six button handlers
 * @returns {JSX.Element} The action bar
 */
export const DashboardTemplateActionBar = ({
  description,
  isEditingLayout,
  onEditDetails,
  onStartEditLayout,
  onAddWidget,
  onCancelLayout,
  onResetLayout,
  onSaveLayout,
}: DashboardTemplateActionBarProps) => {
  const i18n = useTranslation();
  return (
    <Flex justify="space-between" align="center" px="md">
      <Text color={newCOLORS.darkGray} size="regular">
        {description}
      </Text>
      <Group gap="sm">
        {isEditingLayout ? (
          <>
            <Button
              size="sm"
              radius="xl"
              variant="outline"
              color={newCOLORS.darkGray}
              leftSection={<Icon color={newCOLORS.darkGray} name="close" size={16} />}
              onClick={onCancelLayout}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              radius="xl"
              variant="outline"
              color={newCOLORS.darkGray}
              leftSection={<Icon color={newCOLORS.darkGray} name="settings_backup_restore" size={16} />}
              onClick={onResetLayout}
            >
              Reset Layout
            </Button>
            <Button
              size="sm"
              radius="xl"
              variant="primary"
              leftSection={<Icon name="check_circle" size={16} color="white" />}
              onClick={onSaveLayout}
            >
              Save Layout
            </Button>
          </>
        ) : (
          <>
            <Button
              size="sm"
              radius="xl"
              variant="outline"
              color={newCOLORS.darkGray}
              leftSection={<Icon color={newCOLORS.darkGray} name="edit" size={16} />}
              onClick={onEditDetails}
            >
              Edit
            </Button>
            <Button
              size="sm"
              radius="xl"
              variant="outline"
              color={newCOLORS.darkGray}
              leftSection={<Icon color={newCOLORS.darkGray} name="dashboard_customize" size={16} />}
              onClick={onStartEditLayout}
            >
              Edit Layout
            </Button>
            <Button
              size="sm"
              radius="xl"
              variant="primary"
              leftSection={<Icon name="add_circle" size={16} color="white" />}
              onClick={onAddWidget}
            >
              {`Add New ${i18n.t('common.dashboard.widget')}`}
            </Button>
          </>
        )}
      </Group>
    </Flex>
  );
};
